import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Send, ChevronDown, ChevronUp } from "lucide-react";

const AUDIENCE_LABELS = { all: "Everyone", volunteers: "Volunteers only", orgs: "Organisations only" };
const EMPTY = { title: "", body: "", link: "", cta_label: "", audience: "all", send_email: false };

// Admin broadcast composer: in-app bell notifications to a segment, optional
// email (explicit checkbox + recipient-count confirmation). Preview-then-
// confirm flow; history below; server enforces admin-only + 10min cooldown.
export const BroadcastCard = () => {
  const [form, setForm] = useState(EMPTY);
  const [confirming, setConfirming] = useState(false);
  const [counts, setCounts] = useState(null);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const loadHistory = () => api.adminBroadcasts().then(setHistory).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const openConfirm = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and body are required");
    setCounts(null);
    setConfirming(true);
    try { setCounts(await api.adminBroadcastAudienceCount(form.audience)); }
    catch { setCounts({ recipients: "?", emailable: "?" }); }
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await api.adminSendBroadcast(form);
      toast.success(`Broadcast sent to ${r.recipients} ${AUDIENCE_LABELS[r.audience].toLowerCase()}${r.send_email ? ` · ${r.emails_sent} emails` : ""}`);
      setForm(EMPTY);
      setConfirming(false);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send broadcast");
    }
    setSending(false);
  };

  const recall = async (b) => {
    if (!window.confirm(`Recall "${b.title}"? This removes it from every recipient's bell. Emails already sent cannot be recalled.`)) return;
    try {
      const r = await api.adminRecallBroadcast(b.id);
      toast.success(`Recalled — ${r.notifications_removed} notifications removed`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not recall");
    }
  };

  return (
    <div className="mt-14" data-testid="broadcast-card">
      <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
        <Megaphone className="w-5 h-5" /> Broadcast
      </h2>
      <p className="text-neutral-600 text-sm mt-1">One message to a whole audience — in-app bell for everyone, email only if you explicitly tick it.</p>

      <div className="mt-4 bg-white border border-neutral-200 rounded-2xl p-5 space-y-3">
        <Input data-testid="broadcast-title" placeholder="Title — e.g. Welcome to the CivicHearth test!" maxLength={120}
               value={form.title} onChange={(e) => set("title", e.target.value)} />
        <Textarea data-testid="broadcast-body" rows={4} maxLength={1000}
                  placeholder="Body — keep it short and warm. Plain text, line breaks respected."
                  value={form.body} onChange={(e) => set("body", e.target.value)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <Input data-testid="broadcast-link" placeholder="Link (optional) — /opportunities or a URL"
                 value={form.link} onChange={(e) => set("link", e.target.value)} />
          <Input data-testid="broadcast-cta" placeholder="CTA label (optional) — e.g. Explore" maxLength={40}
                 value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} />
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <Select value={form.audience} onValueChange={(v) => set("audience", v)}>
            <SelectTrigger data-testid="broadcast-audience" className="w-52 rounded-full h-10 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(AUDIENCE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
            <Checkbox data-testid="broadcast-email-toggle" checked={form.send_email}
                      onCheckedChange={(v) => set("send_email", !!v)} />
            Also send as email <span className="text-xs text-neutral-400">(respects users' email prefs)</span>
          </label>
          <Button data-testid="broadcast-preview-btn" onClick={openConfirm} className="ml-auto rounded-full bg-forest hover:bg-forest-hover">
            <Send className="w-4 h-4 mr-1.5" /> Preview & send
          </Button>
        </div>
      </div>

      <button data-testid="broadcast-history-toggle" onClick={() => setShowHistory(s => !s)}
              className="mt-3 text-xs text-forest font-medium hover:underline inline-flex items-center gap-1">
        {showHistory ? <>Hide history <ChevronUp className="w-3 h-3" /></> : <>History ({history.length}) <ChevronDown className="w-3 h-3" /></>}
      </button>
      {showHistory && (
        <div className="mt-2 space-y-2" data-testid="broadcast-history">
          {history.length === 0 && <div className="text-sm text-neutral-500">No broadcasts yet.</div>}
          {history.map((b) => (
            <div key={b.id} data-testid={`broadcast-history-${b.id}`} className={`bg-white border border-neutral-200 rounded-xl px-4 py-3 text-sm ${b.recalled ? "opacity-60" : ""}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-medium text-forest">
                  {b.title}
                  {b.recalled && <span data-testid={`broadcast-recalled-${b.id}`} className="ml-2 rounded-full bg-neutral-200 text-neutral-600 text-[10px] px-2 py-px uppercase tracking-wide">Recalled</span>}
                </span>
                <span className="flex items-center gap-3">
                  {!b.recalled && (
                    <button data-testid={`broadcast-recall-${b.id}`} onClick={() => recall(b)}
                            className="text-xs text-red-600 hover:underline">Recall</button>
                  )}
                  <span className="text-xs text-neutral-400">{new Date(b.created_at).toLocaleString()}</span>
                </span>
              </div>
              <div className="text-xs text-neutral-500 mt-1">
                {b.sent_by_name} → {AUDIENCE_LABELS[b.audience] || b.audience} · {b.recipients} in-app
                {b.send_email ? ` · ${b.emails_sent}/${b.emails_attempted} emails` : " · no email"}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent data-testid="broadcast-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Send this broadcast?</DialogTitle>
            <DialogDescription>Exactly what recipients will see in their bell:</DialogDescription>
          </DialogHeader>
          <div className="border border-neutral-200 rounded-xl px-4 py-3 bg-neutral-50">
            <div className="text-sm font-medium text-forest">{form.title}</div>
            <div className="text-xs text-neutral-600 mt-1 whitespace-pre-line">{form.body}</div>
            {form.cta_label && form.link && (
              <span className="mt-2 inline-flex items-center rounded-full bg-forest text-white text-[11px] px-3 py-1">{form.cta_label}</span>
            )}
          </div>
          <div data-testid="broadcast-recipient-count" className="text-sm text-neutral-700">
            {counts === null ? "Counting recipients…" : (
              <>
                Goes to <b>{counts.recipients}</b> {AUDIENCE_LABELS[form.audience].toLowerCase()} in-app
                {form.send_email && <> — and <b>{counts.emailable}</b> real email inboxes (opted-in only)</>}.
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setConfirming(false)}>Cancel</Button>
            <Button data-testid="broadcast-send-confirm" onClick={send} disabled={sending || counts === null}
                    className="rounded-full bg-forest hover:bg-forest-hover">
              {sending ? "Sending…" : `Send broadcast`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
